import React, { useCallback, useEffect, useRef, useState } from 'react';
import bedrockService from '../services/bedrockService';
import SortableResizableTable from './SortableResizableTable';
import SearchableDropdown from './SearchableDropdown';
import DataPointsTable, { flattenDataPoints, getExtractionStatus } from './DataPointsTable';
import { formatTimestamp } from '../utils/dateUtils';
import { resolveTypeName } from '../utils/documentReport';
import { applyCorrections } from '../utils/clientReport';

/**
 * DocumentDataDashboard
 *
 * The Document Data page's "All extractions" tab: lists every document an
 * extraction prompt ran on (optionally filtered by client and doc type),
 * with a link to the source document and expandable rows showing the
 * full key/value extraction.
 */
const DocumentDataDashboard = () => {
  const [extractions, setExtractions] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState('');
  const [docTypeFilter, setDocTypeFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const didFetchDocTypes = useRef(false);

  const typeName = useCallback((id) => resolveTypeName(docTypes, id) || '—', [docTypes]);

  const fetchExtractions = useCallback(async (filters) => {
    setLoading(true);
    setError('');
    try {
      const data = await bedrockService.getExtractions(filters);
      setExtractions(data.extractions || []);
    } catch (err) {
      console.error('Error fetching extractions:', err);
      setError('Failed to load extracted document data.');
      setExtractions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExtractions({});
  }, [fetchExtractions]);

  useEffect(() => {
    if (didFetchDocTypes.current) return;
    didFetchDocTypes.current = true;
    bedrockService.getDocTypes()
      .then((data) => setDocTypes(data.doc_types || []))
      .catch((err) => console.warn('Could not load doc types (names fall back to ids):', err));
    bedrockService.getExtractionClients()
      .then((data) => setClients(data.clients || []))
      .catch((err) => console.warn('Could not load the client list:', err));
  }, []);

  const handleApplyFilters = () => {
    fetchExtractions({
      clientId: (clients.find((c) => c.clientName === clientFilter) || {}).clientId || undefined,
      docType: docTypeFilter || undefined,
    });
  };

  const rows = extractions.map((e) => {
    // Show the values as corrected on the eMoney report.
    const dataPoints = applyCorrections(e.dataPoints, e.annotations && e.annotations.corrections);
    return {
      ...e,
      dataPoints,
      docTypeName: typeName(e.docType),
      subTypeName: typeName(e.subType),
      pointsCount: flattenDataPoints(dataPoints).length,
      extraction: getExtractionStatus(dataPoints, e.dataPointsDetails),
    };
  });

  const columns = [
    { key: 'clientName', label: 'Client' },
    { key: 'documentName', label: 'Document' },
    { key: 'docTypeName', label: 'Type' },
    { key: 'subTypeName', label: 'Sub Type' },
    { key: 'accountNumber', label: 'Account' },
    { key: 'pointsCount', label: '# Data Points' },
    { key: 'extractedAt', label: 'Extracted' },
  ];

  const customRender = {
    documentName: (value, row) => (
      row.url ? (
        <a
          href={row.url}
          target="_blank"
          rel="noopener noreferrer"
          title={row.sharepointLocation || value}
          onClick={(e) => e.stopPropagation()}
        >
          {value || row.fileId}
        </a>
      ) : (
        <span>{value || row.fileId}</span>
      )
    ),
    accountNumber: (value) => value || '—',
    pointsCount: (value, row) => {
      if (row.extraction.kind === 'not_applicable') {
        return <span className="badge bg-secondary" title={row.extraction.reason}>Not applicable</span>;
      }
      if (row.extraction.kind === 'failed') {
        return <span className="badge bg-warning text-dark" title={row.extraction.reason}>Failed</span>;
      }
      return value;
    },
    extractedAt: (value) => (value ? formatTimestamp(value) : '—'),
  };

  return (
    <div data-testid="document-data-dashboard">
      <div className="row g-2 align-items-end mb-3">
        <div className="col-md-4">
          <label className="form-label small mb-1"><strong>Client</strong></label>
          <SearchableDropdown
            options={clients.map((c) => c.clientName)}
            value={clientFilter}
            onChange={setClientFilter}
            placeholder="All clients"
            searchPlaceholder="Search clients..."
          />
        </div>
        <div className="col-md-4">
          <label className="form-label small mb-1"><strong>Document Type</strong></label>
          <select
            className="form-select form-select-sm"
            value={docTypeFilter}
            onChange={(e) => setDocTypeFilter(e.target.value)}
          >
            <option value="">All types</option>
            {docTypes.map((dt) => (<option key={dt.id} value={dt.id}>{dt.name}</option>))}
          </select>
        </div>
        <div className="col-md-4 d-flex gap-2">
          <button className="btn btn-sm btn-primary" onClick={handleApplyFilters} disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Loading...
              </>
            ) : (
              <>
                <i className="bi bi-funnel me-1"></i>Apply
              </>
            )}
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => { setClientFilter(''); setDocTypeFilter(''); fetchExtractions({}); }}
            disabled={loading}
          >
            Clear
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger py-2" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>{error}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="text-center text-muted border rounded p-4" data-testid="document-data-empty">
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Loading extracted data...
            </>
          ) : (
            <>
              <i className="bi bi-database-x fs-4 d-block mb-2"></i>
              No extracted document data yet. Data points appear here after documents are analyzed with an Extract_* prompt.
            </>
          )}
        </div>
      ) : (
        <SortableResizableTable
          columns={columns}
          data={rows}
          customRender={customRender}
          expandableRows={true}
          generateRowId={(row) => row.fileId}
          renderExpandedContent={(row) => (
            <div className="p-2">
              <DataPointsTable dataPoints={row.dataPoints} details={row.dataPointsDetails} />
            </div>
          )}
        />
      )}
    </div>
  );
};

export default DocumentDataDashboard;
